import { useEffect, useMemo, useRef, useState } from 'react';
import { scaleBand, scaleLinear } from '../../lib/scale';
import { fitFontSize, splitAxisLabel } from '../../lib/chartLabels';
import type { BarDatum } from './model';

export interface BarChartProps {
  data: BarDatum[];
  width?: number;
  height?: number;
  /** bars at/above this render at full strength, below are dimmed */
  baseline?: number;
  /** print each bar's value above it */
  showValues?: boolean;
  /** show an axis label every Nth bar (default every bar) */
  labelStep?: number;
  /** formats value labels + tooltips */
  format?: (n: number) => string;
  /** suffix after each value in labels + tooltips (default `×` for normalized ratios) */
  valueSuffix?: string;
  /** label for one unit, used in tooltips (e.g. `ET`) */
  unit?: string;
  className?: string;
  ariaLabel?: string;
  /** append every bar's label and formatted value to `ariaLabel` */
  ariaValues?: boolean;
}

const PAD_X = 34;
const PAD_TOP = 30;
const AXIS_H = 32;
const GAP = 0.18;
const LABEL_SIZE = 11;
const LINE_H = 13;

/** Single-series vertical bars; bars below `baseline` are dimmed to read as under-norm. */
export const BarChart = ({
  data,
  width = 620,
  height = 250,
  baseline = 1,
  showValues = false,
  labelStep = 1,
  format = (n) => n.toFixed(2),
  valueSuffix = '×',
  unit,
  className,
  ariaLabel = 'bar chart',
  ariaValues = false,
}: BarChartProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [boxW, setBoxW] = useState(width);

  useEffect(() => {
    const node = svgRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      const measured = Math.round(entry.contentRect.width);
      if (measured > 0) setBoxW(measured);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const baseY = height - AXIS_H;

  const bars = useMemo(() => {
    const max = Math.max(...data.map((d) => d.value), baseline) || 1;
    const band = scaleBand(data.length, [PAD_X, boxW - PAD_X], GAP);
    const y = scaleLinear([0, max], [baseY, PAD_TOP]);
    return data.map((d, i) => {
      const top = y(d.value);
      const x = band.x(i);
      const lines = splitAxisLabel(d.label, band.bandwidth, LABEL_SIZE);
      return {
        d,
        i,
        x,
        barW: band.bandwidth,
        y: top,
        h: baseY - top,
        cx: x + band.bandwidth / 2,
        opacity: d.value >= baseline ? 1 : 0.5,
        lines,
        labelSize: fitFontSize(lines, band.bandwidth, LABEL_SIZE),
        tip: `${d.label}${unit ? ` ${unit}` : ''}: ${format(d.value)}${valueSuffix}${d.n != null ? ` (n=${d.n})` : ''}`,
      };
    });
  }, [data, boxW, baseY, baseline, format, valueSuffix, unit]);

  const axisOverflow = bars.some((bar) => bar.lines.length > 1) ? LINE_H : 0;
  const label = ariaValues
    ? `${ariaLabel}: ${data.map((d) => `${d.label} ${format(d.value)}${valueSuffix}`).join(', ')}.`
    : ariaLabel;

  return (
    <svg
      ref={svgRef}
      width="100%"
      viewBox={`0 0 ${boxW} ${height + axisOverflow}`}
      role="img"
      aria-label={label}
      className={className}
    >
      <line x1={PAD_X} y1={baseY} x2={boxW - PAD_X} y2={baseY} stroke="var(--line)" />
      {bars.map((b) => (
        <g key={b.i}>
          <rect x={b.x} y={b.y} width={b.barW} height={b.h} rx={2} fill="var(--accent)" fillOpacity={b.opacity}>
            <title>{b.tip}</title>
          </rect>
          {showValues ? (
            <text x={b.cx} y={b.y - 6} textAnchor="middle" fontSize={LABEL_SIZE} fill="var(--text-2)">
              {format(b.d.value)}{valueSuffix}
            </text>
          ) : null}
          {b.i % labelStep === 0 ? (
            <text x={b.cx} y={baseY + 16} textAnchor="middle" fontSize={b.labelSize} fill="var(--text-3)">
              {b.lines.map((line, index) => (
                <tspan key={`${b.i}-${index}-${line}`} x={b.cx} dy={index === 0 ? 0 : LINE_H}>{line}</tspan>
              ))}
            </text>
          ) : null}
        </g>
      ))}
    </svg>
  );
};
